import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/**
 * MB-A SfFileImportModal
 *
 * Opens from the Mission-Briefing tab (gated on canBriefingManage).
 * - On open: GET /v1/projects/:pid/sf-files → list with checkboxes.
 * - Already-imported entries are shown as disabled/marked.
 * - Select → POST /v1/projects/:pid/sf-files/import with { content_version_ids }.
 * - On success: refresh attachment list.
 *
 * Follows the existing MovePhaseModal pattern (fixed-overlay, max-w-xl card).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';
import { Modal } from './modal.js';

export type SfFileImportModalProps = {
  open: boolean;
  onClose: () => void;
  projectId: string;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function SfFileImportModal({ open, onClose, projectId }: SfFileImportModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selection on open
  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const sfFilesQuery = useQuery({
    queryKey: ['v2-sf-files', projectId],
    queryFn: () => api.listSfFiles(projectId),
    enabled: open,
    staleTime: 30_000,
  });

  const importMut = useMutation({
    mutationFn: () => api.importSfFiles(projectId, Array.from(selected)),
    onSuccess: (result) => {
      toast.success(
        t('v2.sf_import.success', 'SF-Dateien importiert'),
        t('v2.sf_import.success_count', '{{count}} Datei(en) übernommen', {
          count: result.imported,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ['v2-attachments', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['v2-sf-files', projectId] });
      onClose();
    },
    onError: (err) =>
      toast.error(
        t('v2.sf_import.failed', 'Import fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  if (!open) return null;

  const items = sfFilesQuery.data?.items ?? [];
  const pendingItems = items.filter((f) => !f.already_imported);
  const alreadyItems = items.filter((f) => f.already_imported);

  function toggleAll() {
    if (selected.size === pendingItems.length && pendingItems.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingItems.map((f) => f.content_version_id)));
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="sf-import-title"
      panelClassName="w-full max-w-xl overflow-hidden"
    >
      {/* Header */}
      <header
        className="px-5 py-4 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--line-soft)' }}
      >
        <div>
          <h2 id="sf-import-title" className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
            {t('v2.sf_import.title', 'SF-Dateien synchronisieren')}
          </h2>
          <p
            className="text-[11px] mt-0.5"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.sf_import.subtitle', 'Salesforce ContentVersions dieses Projekts importieren')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-2 py-1 rounded"
          style={{ color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer' }}
          aria-label={t('v2.briefings_lib.modal_close', 'Schließen')}
        >
          ✕
        </button>
      </header>

      {/* Body */}
      <div className="px-5 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
        {sfFilesQuery.isLoading && (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.detail.loading', 'Lädt…')}
          </p>
        )}

        {!sfFilesQuery.isLoading && items.length === 0 && (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.sf_import.empty', 'Keine SF-Dateien für dieses Projekt gefunden.')}
          </p>
        )}

        {!sfFilesQuery.isLoading && pendingItems.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={toggleAll}
                className="text-[11px]"
                style={{
                  color: 'var(--primary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--f-mono)',
                }}
              >
                {selected.size === pendingItems.length
                  ? t('v2.sf_import.deselect_all', 'Alle abwählen')
                  : t('v2.sf_import.select_all', 'Alle auswählen')}
              </button>
              <span
                className="text-[11px]"
                style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
              >
                {t('v2.sf_import.selected_count', '{{count}} ausgewählt', {
                  count: selected.size,
                })}
              </span>
            </div>
            <div className="space-y-1">
              {pendingItems.map((f) => (
                <label
                  key={f.content_version_id}
                  className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer"
                  style={{
                    background: selected.has(f.content_version_id)
                      ? 'var(--primary-soft)'
                      : 'var(--surface-2)',
                    border: `1px solid ${selected.has(f.content_version_id) ? 'var(--primary)' : 'var(--line-soft)'}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(f.content_version_id)}
                    onChange={() => toggle(f.content_version_id)}
                    className="shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-sm truncate"
                      style={{ color: 'var(--ink)' }}
                      title={f.name}
                    >
                      {f.name}
                    </div>
                    <div
                      className="text-[10px]"
                      style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
                    >
                      {f.type || '—'} · {formatBytes(f.size_bytes)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {!sfFilesQuery.isLoading && alreadyItems.length > 0 && (
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.12em] mb-1 mt-3"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {t('v2.sf_import.already_imported', 'Bereits importiert')}
            </div>
            <div className="space-y-1">
              {alreadyItems.map((f) => (
                <div
                  key={f.content_version_id}
                  className="flex items-center gap-3 px-3 py-2 rounded-md opacity-50"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
                >
                  <input type="checkbox" checked disabled className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-sm truncate"
                      style={{ color: 'var(--ink)' }}
                      title={f.name}
                    >
                      {f.name}
                    </div>
                    <div
                      className="text-[10px]"
                      style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
                    >
                      {f.type || '—'} · {formatBytes(f.size_bytes)}
                      {' · '}
                      <span style={{ color: 'var(--success)' }}>
                        {t('v2.sf_import.already_badge', '✓ importiert')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer
        className="px-5 py-3 border-t flex items-center justify-end gap-2"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
            cursor: 'pointer',
          }}
          disabled={importMut.isPending}
        >
          {t('v2.modal.cancel', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={() => importMut.mutate()}
          disabled={selected.size === 0 || importMut.isPending}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: selected.size === 0 || importMut.isPending ? 0.5 : 1,
            cursor: selected.size === 0 || importMut.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {importMut.isPending
            ? t('v2.sf_import.importing', 'Importiert…')
            : t('v2.sf_import.import_btn', '{{count}} Dateien importieren', {
                count: selected.size,
              })}
        </button>
      </footer>
    </Modal>
  );
}
